import { IModel, IAttribute, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { Activity } from './activity';
import { LinkedService } from '../linkedservice/linkedservice';
import { WranglingDataflow } from '../dataflow/wranglingDataflow';
import { IntegrationRuntime } from '../integrationRuntime/integrationRuntime';
import { AzureBlobStorageLinkedService } from '../linkedservice/azure-blob-storage.linkedservice';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { validateIRConstraints, validateStagingLinkedService } from './execute-dataflow.util';
import { ComputeType } from 'app/shared/constants/dataflow-runtime.options';
import { ParameterValue } from '../base/parameter-value';
import { validateEntityParameters } from 'app/shared/utils/activity-validation.util';
import {
    AzureSqlDWTableDataset,
    AzureStorageLinkedService,
    IValidationContext,
    ModelConstants,
    OwnedSet,
    SelfHostedIntegrationRuntime
} from 'shell-models-lib';
import { PowerQuerySink } from './power-query-sink';
import { SynapseAnalyticsSettings } from '../dataflow/dataset-settings/synapse-analytics-settings';
import { HiveSettings } from '../dataflow/dataset-settings/hive-dataset-settings';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@Metamodel.model({
    name: ModelConstants.executeWranglingDataflowActivity,
    display: [stringResources.PowerQuery, stringResources.ModelName_PowerQueryActivities],
    referable: true
})
export class ExecuteWranglingDataflowActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly STAGING_LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly STAGING_STORAGE_PATH: IAttribute;
    public static readonly COMPUTE_TYPE: IAttribute;
    public static readonly CORE_COUNT: IAttribute;
    public static readonly DATAFLOW_DATASET_PARAMETERS: IAttribute;

    @Metamodel.attribute({
        display: stringResources.DataFlowComputeTypeLabel,
        constraint: {
            required: true
        },
        templatable: true,
        association: 'COMPUTE_TYPE'
    })
    public computeType?: ComputeType = 'General';

    @Metamodel.attribute({
        display: stringResources.DataFlowCoreCountLabel,
        constraint: {
            required: true,
            minimum: 8,
            whole: true
        },
        templatable: true,
        association: 'CORE_COUNT'
    })
    public coreCount?: number = 8;

    @Metamodel.attribute({
        display: stringResources.IntegrationRuntime,
        reference: true,
        type: IntegrationRuntime.MODEL,
        constraint: {
            validator: ExecuteWranglingDataflowActivity.validateIRContraints
        }
    })
    public integrationRuntime: IntegrationRuntime;

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingLinkedService,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: ExecuteWranglingDataflowActivity.validateStagingLinkedService
        }
    })
    public stagingLinkedService?: LinkedService;

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingPath,
        constraint: {
            validator: ExecuteWranglingDataflowActivity.validateStagingPath
        },
        association: 'STAGING_STORAGE_PATH'
    })
    public stagingStoragePath?: string;

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'STAGING_LINKED_SERVICE_PARAMETERS'
    })
    public stagingLinkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        collection: Collection.Set,
        constraint: {
            required: false,
            validator: ExecuteWranglingDataflowActivity.validateDatasetParameters
        },
        association: 'DATAFLOW_DATASET_PARAMETERS'
    })
    public dataflowDatasetParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    @Metamodel.attribute({
        display: stringResources.DataFlow,
        constraint: {
            required: true
        },
        reference: true,
        type: WranglingDataflow.MODEL
    })
    public wranglingDataflow?: WranglingDataflow;

    @Metamodel.attribute({
        type: PowerQuerySink.MODEL,
        constraint: {
            validator: ExecuteWranglingDataflowActivity.validateSinks
        },
        collection: Collection.Set
    })
    public queries = OwnedSet.create<PowerQuerySink>(this);

    public stagingLinkedServicesTypes: string[] = [
        Metamodel.modelFor(AzureBlobStorageLinkedService).name,
        Metamodel.modelFor(AzureStorageLinkedService).name
    ];

    public dataFlowDebugSessionId?: string;

    public static validateDatasetParameters(dataflowActivity: ExecuteWranglingDataflowActivity): IViolation[] | undefined {
        const violation: IViolation[] = [];
        if (dataflowActivity && dataflowActivity.wranglingDataflow) {
            dataflowActivity.wranglingDataflow.sources.forEach((source) => {
                violation.push(...validateEntityParameters(source.sourceDataset, dataflowActivity.dataflowDatasetParameters.get(source.name) || [], dataflowActivity, source));
            });
        }
        return violation;
    }

    public static async validateIRContraints(dataflowActivity: ExecuteWranglingDataflowActivity, value: IntegrationRuntime, violation?: IViolation, context?: IValidationContext): Promise<IViolation[]> {
          return validateIRConstraints(dataflowActivity, dataflowActivity.integrationRuntime, violation, context);
    }

    public static validateStagingLinkedService(dataflowActivity: ExecuteWranglingDataflowActivity): IViolation[] | undefined {
        const stagingRequired = ExecuteWranglingDataflowActivity.StagingRequired(dataflowActivity);
        const attributeName = ExecuteWranglingDataflowActivity.MODEL.attribute('stagingLinkedService').name;
        return validateStagingLinkedService(dataflowActivity, stagingRequired, attributeName);
    }

    // Validate the following:
    // 1. There is at least 1 sink
    // 2. Dataset models are supported
    // 3. Referencing managed IR based LS only
    public static async validateSinks(dataflowActivity: ExecuteWranglingDataflowActivity, value: OwnedSet<PowerQuerySink>, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        if (dataflowActivity.queries.size === 0) {
            return [{
                context: dataflowActivity,
                message: () => stringResources.WranglingDataFlowErrorNoSink
            }];
        }
        const violations: IViolation[] = [];
        const supportedSinkModelNames = (await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService')).getSupportedDataflowSinkModelNames();
        dataflowActivity.queries.forEach((pqSink) => {
            if (pqSink.sinks) {
                pqSink.sinks.forEach((sink) => {
                    const dataset = sink.sinkDataset;
                    if (!supportedSinkModelNames.includes(Metamodel.modelOf(dataset).name)) {
                        violations.push({
                            context: dataflowActivity,
                            message: () => sanitizeAndFormatString(stringResources.WranglingDataflowUnsupportedDataset, dataset.name, Metamodel.modelOf(dataset).name, supportedSinkModelNames.join())
                        });
                    }
                    if (dataset.linkedService && dataset.linkedService.integrationRuntime instanceof SelfHostedIntegrationRuntime) {
                        violations.push({
                            context: dataflowActivity,
                            message: () => sanitizeAndFormatString(stringResources.WranglingDataflowLinkedServiceIRError, dataset.name, dataset.linkedService.name)
                        });
                    }
                });
            }
        });
        return violations;
    }

    public static validateStagingPath(dataflowActivity: ExecuteWranglingDataflowActivity): IViolation[] | undefined {
        if (ExecuteWranglingDataflowActivity.StagingRequired(dataflowActivity) && !dataflowActivity.stagingStoragePath) {
            return [{
                context: dataflowActivity,
                message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, Metamodel.modelOf(dataflowActivity).display[0],
                    dataflowActivity.name ? '\'' + dataflowActivity.name + '\'' : '', ExecuteWranglingDataflowActivity.MODEL.attribute('stagingStoragePath').name)
            }];
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public static StagingRequired(dataflowActivity: ExecuteWranglingDataflowActivity): boolean {
        if (dataflowActivity && dataflowActivity.wranglingDataflow) {
            const sourceRequired = !!dataflowActivity.wranglingDataflow.sources.find((source) => {
                return source.sourceDataset && (source.staged && source.sourceDataset instanceof AzureSqlDWTableDataset) ||
                    source.datasetSettings && (source.datasetSettings instanceof SynapseAnalyticsSettings &&
                        !(source.datasetSettings instanceof HiveSettings) && source.datasetSettings.staged);
            });
            const sinkRequired = !!dataflowActivity.queries.find((query) => {
                return !!query.sinks.find((sink) => {
                    return sink.sinkDataset && (sink.staged && sink.sinkDataset instanceof AzureSqlDWTableDataset) ||
                    sink.datasetSettings && (sink.datasetSettings instanceof SynapseAnalyticsSettings &&
                        !(sink.datasetSettings instanceof HiveSettings) && sink.datasetSettings.staged);
                });
            });

            return sourceRequired || sinkRequired;
        }
        return false;
    }
}
